import { getPur, pageParams, PurClientConfig, readDecimal, readId, readObject, readPage, readText, requireId } from "./purRead";

export type { PurClientConfig } from "./purRead";
export type PurResource = "materialRequests" | "purchaseOrders" | "receipts";

interface PurLineBase {
  id: number | null;
  productId: number | null;
  unitId: number | null;
  productName: string;
  unitName: string;
  conversionFactor: string | null;
  stockQuantity: string | null;
}

export interface PurRequestLine extends PurLineBase {
  kind: "materialRequests";
  requestId: number | null;
  poItemId: number | null;
  requestedQuantity: string | null;
  orderedQuantity: string | null;
}

export interface PurOrderLine extends PurLineBase {
  kind: "purchaseOrders";
  // Hydrated from the product master when preparing a receipt, not from PO data.
  hasLots?: boolean | null;
  poId: number | null;
  orderedQuantity: string | null;
  receivedQuantity: string | null;
  invoicedQuantity: string | null;
  billedAmount: string | null;
  unitCost: string | null;
  baseUnitCost: string | null;
}

export interface PurReceiptLine extends PurLineBase {
  kind: "receipts";
  receiptId: number | null;
  poItemId: number | null;
  productLotId: number | null;
  deliveredQuantity: string | null;
  acceptedQuantity: string | null;
  rejectedQuantity: string | null;
  rejectedStockQuantity: string | null;
  rejectionReason: string;
  unitCost: string | null;
  baseUnitCost: string | null;
  batchNumber: string;
  manufactureDate: string;
  expiryDate: string;
}

export type PurLine = PurRequestLine | PurOrderLine | PurReceiptLine;

export interface PurRecord {
  id: number;
  number: string;
  status: string;
  date: string;
  createdAt: string;
  editedAt: string;
  requestingDivisionId: number | null;
  vendorId: number | null;
  vendorName: string;
  projectId: number | null;
  projectNumber: string;
  poId: number | null;
  storageId: string;
  storageName: string;
  rejectedStorageId: number | null;
  rejectedStorageName: string;
  percentReceived: string | null;
  percentBilled: string | null;
  totalAmount: string | null;
  purpose: string;
  notes: string;
  itemCount: number | null;
  items: PurLine[];
}

export interface PurPage { count: number; records: PurRecord[] }
export interface PurListQuery { limit: number; offset: number; status?: string }

const RESOURCE_PATH: Record<PurResource, string> = {
  materialRequests: "material-requests", purchaseOrders: "purchase-orders", receipts: "receipts",
};

const STATUS_FILTER_CODE: Record<PurResource, Record<string, number>> = {
  materialRequests: { DRAFT: 10, SUBMITTED: 20, APPROVED: 30, PARTIALLY_ORDERED: 40, ORDERED: 50, CANCELLED: 90 },
  purchaseOrders: { DRAFT: 10, PENDING_APPROVAL: 20, APPROVED: 30, PARTIALLY_RECEIVED: 40, TO_BILL: 45, TO_RECEIVE: 46, COMPLETED: 50, CANCELLED: 90 },
  receipts: { DRAFT: 10, VERIFIED: 20, CANCELLED: 90 },
};

export const normalizePurLine = (resource: PurResource, value: unknown): PurLine => {
  const item = readObject(value);
  const base: PurLineBase = {
    id: readId(item.id), productId: readId(item.product), unitId: readId(item.unit),
    productName: readText(item.product_name) || readText(item.item_description),
    unitName: readText(item.unit_name),
    conversionFactor: readDecimal(item.conversion_factor), stockQuantity: readDecimal(item.stock_qty),
  };
  switch (resource) {
    case "materialRequests": return {
      ...base, kind: resource, requestId: readId(item.request), poItemId: readId(item.po_item),
      requestedQuantity: readDecimal(item.requested_qty), orderedQuantity: readDecimal(item.ordered_qty),
    };
    case "purchaseOrders": return {
      ...base, kind: resource, poId: readId(item.po),
      orderedQuantity: readDecimal(item.ordered_qty), receivedQuantity: readDecimal(item.received_qty),
      invoicedQuantity: readDecimal(item.invoiced_qty), billedAmount: readDecimal(item.billed_amount),
      unitCost: readDecimal(item.unit_cost), baseUnitCost: readDecimal(item.base_unit_cost),
    };
    case "receipts": return {
      ...base, kind: resource, receiptId: readId(item.receipt), poItemId: readId(item.po_item), productLotId: readId(item.product_lot),
      // GR received_qty includes rejected goods; PO received_qty means accepted.
      deliveredQuantity: readDecimal(item.received_qty), acceptedQuantity: readDecimal(item.accepted_qty),
      rejectedQuantity: readDecimal(item.rejected_qty), rejectedStockQuantity: readDecimal(item.rejected_stock_qty),
      rejectionReason: readText(item.rejection_reason),
      unitCost: readDecimal(item.unit_cost), baseUnitCost: readDecimal(item.base_unit_cost),
      batchNumber: readText(item.batch_no), manufactureDate: readText(item.mfg_date), expiryDate: readText(item.exp_date),
    };
  }
};

const normalizeStatus = (resource: PurResource, value: unknown): string => {
  const raw = readText(value);
  return Object.entries(STATUS_FILTER_CODE[resource]).find(([, code]) => String(code) === raw)?.[0] || raw;
};

export const normalizePurRecord = (resource: PurResource, value: unknown): PurRecord => {
  const item = readObject(value);
  const id = requireId(item.id);
  const numberField = { materialRequests: "request_number", purchaseOrders: "po_number", receipts: "grn_number" }[resource];
  const dateField = { materialRequests: "request_date", purchaseOrders: "order_date", receipts: "receipt_date" }[resource];
  return {
    id, number: readText(item[numberField]) || String(id), status: normalizeStatus(resource, item.status),
    date: readText(item[dateField]), createdAt: readText(item.created), editedAt: readText(item.edited),
    requestingDivisionId: readId(item.requesting_division), vendorId: readId(item.vendor), vendorName: readText(item.vendor_name),
    projectId: readId(item.project), projectNumber: readText(item.project_number), poId: readId(item.po),
    storageId: readText(item.storage ?? item.delivery_storage ?? item.target_storage), storageName: readText(item.storage_name),
    rejectedStorageId: readId(item.rejected_storage), rejectedStorageName: readText(item.rejected_storage_name),
    percentReceived: resource === "purchaseOrders" ? readDecimal(item.per_received) : null,
    percentBilled: resource === "purchaseOrders" ? readDecimal(item.per_billed) : null,
    totalAmount: readDecimal(item.total_amount), purpose: readText(item.purpose), notes: readText(item.notes),
    itemCount: Array.isArray(item.items) ? item.items.length : null,
    items: Array.isArray(item.items) ? item.items.map((line) => normalizePurLine(resource, line)) : [],
  };
};

export const normalizePurPage = (resource: PurResource, value: unknown): PurPage =>
  readPage(value, (row) => normalizePurRecord(resource, row));

export const listPurRecords = async (config: PurClientConfig, resource: PurResource, query: PurListQuery): Promise<PurPage> => {
  const params = pageParams(query.limit, query.offset);
  if (query.status) {
    const code = STATUS_FILTER_CODE[resource][query.status];
    if (code === undefined) throw new Error(`Unsupported PUR status: ${query.status}`);
    params.set("status", String(code));
  }
  return normalizePurPage(resource, await getPur(config, `${RESOURCE_PATH[resource]}/?${params.toString()}`));
};

export const getPurRecord = async (config: PurClientConfig, resource: PurResource, id: number): Promise<PurRecord> =>
  normalizePurRecord(resource, await getPur(config, `${RESOURCE_PATH[resource]}/${requireId(id)}/`));
